import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Toast from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

const SUCCESS_TOAST_MS = 4000;
const ERROR_TOAST_MS = 10000;

export const NotificationProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const confirmResolveRef = useRef(null);

  const showSuccess = useCallback((message) => {
    setToast({ type: 'success', message, durationMs: SUCCESS_TOAST_MS });
  }, []);

  const showError = useCallback((message) => {
    setToast({ type: 'error', message, durationMs: ERROR_TOAST_MS });
  }, []);

  const confirm = useCallback((options) => {
    const {
      title = 'Confirm',
      message = 'Are you sure?',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      danger = false
    } = typeof options === 'string' ? { message: options } : options;

    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({
        title,
        message,
        confirmText,
        cancelText,
        danger
      });
    });
  }, []);

  const handleConfirmClose = useCallback((confirmed) => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(confirmed);
      confirmResolveRef.current = null;
    }
    setConfirmState(null);
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const value = {
    showSuccess,
    showError,
    confirm
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          durationMs={toast.durationMs}
          onDismiss={dismissToast}
        />
      )}
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          danger={confirmState.danger}
          onConfirm={() => handleConfirmClose(true)}
          onCancel={() => handleConfirmClose(false)}
        />
      )}
    </NotificationContext.Provider>
  );
};
